import type { Locale } from '../types'

/**
 * Engagement system copy: smart reminders, streak protection, Baú da Colmeia,
 * Minha Colmeia, community challenges, weekly league, recap, progress photos,
 * share cards, fast first check and future automatic input.
 * Tone: supportive, never shame, never loss-aversion pressure.
 */
export const engagementStrings: Record<Locale, Record<string, string>> = {
  'pt-BR': {
    /* Smart reminders */
    'eng.rem.title': 'Lembretes inteligentes',
    'eng.rem.subtitle': 'Avisos no seu ritmo, nunca em excesso.',
    'eng.rem.entry': 'Lembretes inteligentes',
    'eng.rem.entryHint': 'Horário adaptado e frequência',
    'eng.rem.usual': 'Seu horário habitual de Daily Check',
    'eng.rem.usualHint': 'Calculado a partir dos seus últimos {n} Daily Checks.',
    'eng.rem.next': 'Lembrete às {time}',
    'eng.rem.fixedNext': 'Lembrete às {time} (horário fixo)',
    'eng.rem.smart': 'Adaptar ao meu horário',
    'eng.rem.smartHint': 'Desligado, o lembrete usa o horário fixo das {time}.',
    'eng.rem.frequency': 'Frequência',
    'eng.rem.freq.essential': 'Essencial',
    'eng.rem.freq.balanced': 'Equilibrado',
    'eng.rem.freq.all': 'Tudo',
    'eng.rem.freqHint.essential': 'Só o lembrete do Daily Check.',
    'eng.rem.freqHint.balanced': 'Daily Check, desafios e um resumo social por dia.',
    'eng.rem.freqHint.all': 'Inclui curtidas e comentários, sempre agrupados.',
    'eng.rem.cap': 'No máximo {n} por dia',
    'eng.rem.quiet': 'Silêncio noturno',
    'eng.rem.quietHint': 'Nada é enviado entre {from} e {to}.',
    'eng.rem.examples': 'Como os avisos chegam',
    'eng.rem.sent': 'Enviado nesta frequência',
    'eng.rem.notSent': 'Não enviado nesta frequência',
    'eng.rem.kind.dailyCheck': 'Daily Check',
    'eng.rem.kind.social': 'Comunidade',
    'eng.rem.kind.reaction': 'Curtidas e comentários',
    'eng.rem.kind.challenge': 'Desafio',
    'eng.rem.note': 'Lembretes de saúde sempre vêm antes do social. Cada categoria pode ser desligada em Notificações.',

    /* Streak protection */
    'eng.prot.title': 'Proteção de Sequência',
    'eng.prot.subtitle': 'Um dia difícil não apaga semanas de cuidado.',
    'eng.prot.entry': 'Proteção de Sequência',
    'eng.prot.available': '{n} proteção disponível',
    'eng.prot.none': 'Nenhuma proteção disponível',
    'eng.prot.off': 'Proteção desligada',
    'eng.prot.toggle': 'Usar Proteção de Sequência',
    'eng.prot.toggleHint': 'Opcional. Você pode desligar quando quiser.',
    'eng.prot.how': 'Como funciona',
    'eng.prot.rule1': 'Se você perder um dia, a sequência continua em vez de recomeçar.',
    'eng.prot.rule2': 'O dia protegido não conta como concluído: não gera Bee e não altera o Life Score.',
    'eng.prot.rule3': 'A proteção recarrega depois de {n} dias concluídos seguidos.',
    'eng.prot.rule4': 'Vale para sequências de {n} dias ou mais.',
    'eng.prot.example': 'Exemplo de semana',
    'eng.prot.legend.done': 'Concluído',
    'eng.prot.legend.protected': 'Protegido',
    'eng.prot.legend.none': 'Sem registro',
    'eng.prot.dayDone': '{day}: concluído',
    'eng.prot.dayProtected': '{day}: dia protegido, não concluído',
    'eng.prot.dayNone': '{day}: sem registro',
    'eng.heatmap.protected': 'Protegido',
    'eng.heatmap.cellProtected': '{date}: dia protegido, sem registro',

    /* Hive */
    'eng.hive.title': 'Minha Colmeia',
    'eng.hive.subtitle': 'Cresce com o Bee dos seus hábitos.',
    'eng.hive.entry': 'Minha Colmeia',
    'eng.hive.total': '{n} Bee acumulados',
    'eng.hive.stage': 'Estágio atual',
    'eng.hive.next': 'Faltam {n} Bee para {stage}',
    'eng.hive.max': 'Você chegou ao último estágio.',
    'eng.hive.cells': '{filled} de {total} células preenchidas',
    'eng.hive.note': 'Cada célula representa {n} Bee. A colmeia não altera seu Life Score.',
    'eng.hive.aria': 'Colmeia com {filled} células preenchidas',
    'eng.hive.collectibles': 'Elementos da colmeia',
    'eng.hive.unlocked': 'Conquistado',
    'eng.hive.locked': 'Ainda não',
    'eng.hive.stages': 'Estágios',

    /* Chest */
    'eng.chest.title': 'Baú da Colmeia',
    'eng.chest.ready': 'Um presente da colmeia chegou',
    'eng.chest.open': 'Abrir o baú',
    'eng.chest.opened': 'Presente recebido',
    'eng.chest.bonus': '+{n} Bee de bônus',
    'eng.chest.bonusNote': 'Fora da base de 100 Bee do Daily Check.',
    'eng.chest.rules': 'Aparece de vez em quando, ligado a conquistas reais, com frequência limitada. Nunca envolve compras.',
    'eng.chest.empty': 'Nenhum baú por agora. Ele aparece quando você alcança algo significativo.',
    'eng.chest.reason.streakMilestone': 'Pela sua sequência de {n} dias ou mais.',
    'eng.chest.reason.perfectDay': 'Por um dia perfeito no Daily Check.',
    'eng.chest.reason.challengeDone': 'Por concluir um desafio de comunidade.',
    'eng.chest.reason.returning': 'Pela sua volta à rotina.',
    'eng.chest.sample': 'Exemplo',
    'eng.chest.sampleNote': 'Valor e marco de exemplo. A recompensa real fica entre {min} e {max} Bee, conforme as regras ativas.',

    /* Challenges */
    'eng.ch.tab': 'Desafios',
    'eng.ch.weekly': 'Desafios da semana',
    'eng.ch.optional': 'Participar é opcional. Seu progresso só aparece para o grupo se você escolher compartilhar.',
    'eng.ch.join': 'Participar',
    'eng.ch.joined': 'Participando',
    'eng.ch.participants': '{n} participantes',
    'eng.ch.ends': 'Termina em {n} dias',
    'eng.ch.progress': '{current} de {target} {unit}',
    'eng.ch.done': 'Desafio concluído',
    'eng.ch.share': 'Compartilhar conclusão',
    'eng.ch.shared': 'Conclusão compartilhada',
    'eng.ch.unit.distanceKm': 'km',
    'eng.ch.unit.swimKm': 'km',
    'eng.ch.unit.workouts': 'treinos',
    'eng.ch.unit.mobilitySessions': 'sessões',
    'eng.ch.unit.checkDays': 'dias',
    'eng.ch.none': 'Nenhum desafio nesta comunidade por enquanto.',
    'eng.ch.noneBody': 'Novos desafios começam a cada semana.',

    /* League */
    'eng.league.title': 'Liga semanal',
    'eng.league.subtitle': 'Um grupo pequeno, só por consistência.',
    'eng.league.entry': 'Liga semanal',
    'eng.league.introTitle': 'Sua liga é opcional',
    'eng.league.introBody': 'Você entra em um grupo de {n} pessoas com ritmo parecido. A única medida é quantos dias você fez o Daily Check na semana.',
    'eng.league.p1': 'Sem ranking global.',
    'eng.league.p2': 'Nunca compara Life Score, corpo ou dados de saúde.',
    'eng.league.p3': 'Recomeça a cada ciclo ({day}). Sem rebaixamento.',
    'eng.weekday.mon': 'segunda',
    'eng.weekday.tue': 'terça',
    'eng.weekday.wed': 'quarta',
    'eng.weekday.thu': 'quinta',
    'eng.weekday.fri': 'sexta',
    'eng.weekday.sat': 'sábado',
    'eng.weekday.sun': 'domingo',
    'eng.league.join': 'Participar desta semana',
    'eng.league.leave': 'Sair da liga',
    'eng.league.group': 'Seu grupo desta semana',
    'eng.league.days': '{n} de {total} dias',
    'eng.league.you': 'Você',
    'eng.league.metric': 'Dias com Daily Check nesta semana',
    'eng.league.note': 'Todo o grupo celebra junto a consistência no fim do ciclo.',

    /* Recap */
    'eng.recap.title': 'Retrospectiva',
    'eng.recap.subtitle': 'Seu período em um olhar.',
    'eng.recap.entry': 'Retrospectiva do mês e do ano',
    'eng.recap.month': 'Mês',
    'eng.recap.year': 'Ano',
    'eng.recap.avg': 'Life Score médio',
    'eng.recap.streak': 'Maior sequência',
    'eng.recap.streakValue': '{n} dias',
    'eng.recap.checkDays': 'Dias com Daily Check',
    'eng.recap.habit': 'Hábito mais consistente',
    'eng.recap.activity': 'Atividade principal',
    'eng.recap.community': 'Na comunidade',
    'eng.recap.communityValue': '{posts} posts · {challenges} desafios',
    'eng.recap.highlights': 'Destaques',
    'eng.recap.card': 'Seu card para compartilhar',
    'eng.recap.share': 'Compartilhar retrospectiva',

    /* Share */
    'eng.share.title': 'Compartilhar Life Score',
    'eng.share.subtitle': 'Você decide se e onde compartilhar.',
    'eng.share.entry': 'Compartilhar Life Score',
    'eng.share.today': 'Life Score de hoje',
    'eng.share.streak': 'Sequência de {n} dias',
    'eng.share.includeStreak': 'Incluir sequência',
    'eng.share.includeHabits': 'Incluir hábitos concluídos',
    'eng.share.habits': '{n} de 6 hábitos',
    'eng.share.privacy': 'Só entra no card o que você ligar aqui. Métricas corporais, biomarcadores e humor nunca são incluídos.',
    'eng.share.button': 'Compartilhar',
    'eng.share.sheet': 'Compartilhar em',
    'eng.share.stories': 'Instagram Stories',
    'eng.share.whatsapp': 'WhatsApp',
    'eng.share.save': 'Salvar imagem',
    'eng.share.ready': 'Imagem pronta',
    'eng.share.readyBody': 'No app, a imagem é entregue ao {target} pelo compartilhamento do sistema. Nada é publicado sem você confirmar lá.',
    'eng.share.format': 'Formato 9:16, ideal para Stories e status.',

    /* Progress photos */
    'eng.photos.title': 'Fotos de evolução',
    'eng.photos.subtitle': 'Privadas por padrão. Você decide quando compartilhar.',
    'eng.first.hydrationNoTarget': 'Meta personalizada ainda não calculada',
    'eng.first.hydrationNoTargetHint':
      'Adicione seu peso quando quiser para calcular sua meta diária de hidratação.',
    'eng.photos.entry': 'Fotos de evolução',
    'eng.photos.private': 'Privado',
    'eng.photos.privateNote': 'Estas fotos ficam privadas por padrão e não aparecem no seu perfil, na comunidade nem no Admin.',
    'eng.photos.compare': 'Comparar',
    'eng.photos.first': 'Início',
    'eng.photos.latest': 'Mais recente',
    'eng.photos.timeline': 'Linha do tempo',
    'eng.photos.add': 'Adicionar foto',
    'eng.photos.share': 'Compartilhar',
    'eng.photos.shareTitle': 'Compartilhar esta foto?',
    'eng.photos.shareBody': 'Ela sairá do espaço privado e ficará visível para quem você escolher no próximo passo. Você pode apagar depois.',
    'eng.photos.shareConfirm': 'Sim, escolher onde compartilhar',
    'eng.photos.shareCancel': 'Manter privada',
    'eng.photos.alt': 'Foto de evolução de {date}',
    'eng.photos.addedNote': 'No app, a câmera ou a galeria abrem aqui. A foto entra privada.',

    /* Fast first check */
    'eng.first.title': 'Seu primeiro Daily Check',
    'eng.first.body': 'Toque no que você já fez hoje. Leva menos de um minuto.',
    'eng.first.mood': 'Como você está agora?',
    'eng.first.progress': '{n} Bee hoje',
    'eng.first.continue': 'Concluir e continuar',
    'eng.first.skip': 'Fazer depois',
    'eng.first.hint': 'Tudo pode ser ajustado depois, na aba Check.',

    /* Future automatic input */
    'eng.auto.title': 'Preenchimento automático de hábitos',
    'eng.auto.body': 'No futuro, Apple Health e Google Health Connect poderão sugerir hábitos concluídos. Nada é ativado agora.',
    'eng.auto.confirm': 'Quando disponível, você confirma cada sugestão antes de qualquer Bee ser contado, e pode desligar a qualquer momento.',
    'eng.auto.from': 'De: {providers}',
  },
  'en-US': {
    'eng.rem.title': 'Smart reminders',
    'eng.rem.subtitle': 'Reminders at your pace, never too many.',
    'eng.rem.entry': 'Smart reminders',
    'eng.rem.entryHint': 'Adaptive timing and frequency',
    'eng.rem.usual': 'Your usual Daily Check time',
    'eng.rem.usualHint': 'Based on your last {n} Daily Checks.',
    'eng.rem.next': 'Reminder at {time}',
    'eng.rem.fixedNext': 'Reminder at {time} (fixed time)',
    'eng.rem.smart': 'Adapt to my routine',
    'eng.rem.smartHint': 'When off, the reminder uses the fixed {time} time.',
    'eng.rem.frequency': 'Frequency',
    'eng.rem.freq.essential': 'Essential',
    'eng.rem.freq.balanced': 'Balanced',
    'eng.rem.freq.all': 'Everything',
    'eng.rem.freqHint.essential': 'Only the Daily Check reminder.',
    'eng.rem.freqHint.balanced': 'Daily Check, challenges and one social digest a day.',
    'eng.rem.freqHint.all': 'Adds likes and comments, always bundled.',
    'eng.rem.cap': 'At most {n} a day',
    'eng.rem.quiet': 'Quiet hours',
    'eng.rem.quietHint': 'Nothing is sent between {from} and {to}.',
    'eng.rem.examples': 'How reminders arrive',
    'eng.rem.sent': 'Sent at this frequency',
    'eng.rem.notSent': 'Not sent at this frequency',
    'eng.rem.kind.dailyCheck': 'Daily Check',
    'eng.rem.kind.social': 'Community',
    'eng.rem.kind.reaction': 'Likes and comments',
    'eng.rem.kind.challenge': 'Challenge',
    'eng.rem.note': 'Health reminders always come before social ones. Each category can be turned off in Notifications.',

    'eng.prot.title': 'Streak Protection',
    'eng.prot.subtitle': "One hard day doesn't erase weeks of care.",
    'eng.prot.entry': 'Streak Protection',
    'eng.prot.available': '{n} protection available',
    'eng.prot.none': 'No protection available',
    'eng.prot.off': 'Protection off',
    'eng.prot.toggle': 'Use Streak Protection',
    'eng.prot.toggleHint': 'Optional. Turn it off anytime.',
    'eng.prot.how': 'How it works',
    'eng.prot.rule1': 'If you miss a day, your streak continues instead of restarting.',
    'eng.prot.rule2': "The protected day doesn't count as completed: it earns no Bee and doesn't change Life Score.",
    'eng.prot.rule3': 'Protection recharges after {n} completed days in a row.',
    'eng.prot.rule4': 'Applies to streaks of {n} days or more.',
    'eng.prot.example': 'Example week',
    'eng.prot.legend.done': 'Completed',
    'eng.prot.legend.protected': 'Protected',
    'eng.prot.legend.none': 'No record',
    'eng.prot.dayDone': '{day}: completed',
    'eng.prot.dayProtected': '{day}: protected day, not completed',
    'eng.prot.dayNone': '{day}: no record',
    'eng.heatmap.protected': 'Protected',
    'eng.heatmap.cellProtected': '{date}: protected day, no record',

    'eng.hive.title': 'My Hive',
    'eng.hive.subtitle': 'It grows with the Bee from your habits.',
    'eng.hive.entry': 'My Hive',
    'eng.hive.total': '{n} Bee collected',
    'eng.hive.stage': 'Current stage',
    'eng.hive.next': '{n} Bee to {stage}',
    'eng.hive.max': "You've reached the final stage.",
    'eng.hive.cells': '{filled} of {total} cells filled',
    'eng.hive.note': "Each cell is {n} Bee. Your hive doesn't change your Life Score.",
    'eng.hive.aria': 'Hive with {filled} filled cells',
    'eng.hive.collectibles': 'Hive elements',
    'eng.hive.unlocked': 'Earned',
    'eng.hive.locked': 'Not yet',
    'eng.hive.stages': 'Stages',

    'eng.chest.title': 'Hive Chest',
    'eng.chest.ready': 'A gift from the hive arrived',
    'eng.chest.open': 'Open the chest',
    'eng.chest.opened': 'Gift received',
    'eng.chest.bonus': '+{n} bonus Bee',
    'eng.chest.bonusNote': 'Outside the 100 Bee Daily Check base.',
    'eng.chest.rules': 'Appears now and then, tied to real achievements, with limited frequency. Never involves purchases.',
    'eng.chest.empty': 'No chest right now. It appears when you reach something meaningful.',
    'eng.chest.reason.streakMilestone': 'For keeping a {n}+ day streak.',
    'eng.chest.reason.perfectDay': 'For a perfect Daily Check day.',
    'eng.chest.reason.challengeDone': 'For finishing a community challenge.',
    'eng.chest.reason.returning': 'For coming back to your routine.',
    'eng.chest.sample': 'Sample',
    'eng.chest.sampleNote': 'Sample amount and milestone. The real reward is between {min} and {max} Bee, based on the active rules.',

    'eng.ch.tab': 'Challenges',
    'eng.ch.weekly': "This week's challenges",
    'eng.ch.optional': 'Joining is optional. Your progress is only shown to the group if you choose to share it.',
    'eng.ch.join': 'Join',
    'eng.ch.joined': 'Joined',
    'eng.ch.participants': '{n} participants',
    'eng.ch.ends': 'Ends in {n} days',
    'eng.ch.progress': '{current} of {target} {unit}',
    'eng.ch.done': 'Challenge complete',
    'eng.ch.share': 'Share completion',
    'eng.ch.shared': 'Completion shared',
    'eng.ch.unit.distanceKm': 'km',
    'eng.ch.unit.swimKm': 'km',
    'eng.ch.unit.workouts': 'workouts',
    'eng.ch.unit.mobilitySessions': 'sessions',
    'eng.ch.unit.checkDays': 'days',
    'eng.ch.none': 'No challenges in this community yet.',
    'eng.ch.noneBody': 'New challenges start every week.',

    'eng.league.title': 'Weekly league',
    'eng.league.subtitle': 'A small group, for consistency only.',
    'eng.league.entry': 'Weekly league',
    'eng.league.introTitle': 'Your league is optional',
    'eng.league.introBody': "You join a group of {n} people at a similar pace. The only measure is how many days you did your Daily Check this week.",
    'eng.league.p1': 'No global ranking.',
    'eng.league.p2': 'Never compares Life Score, body or health data.',
    'eng.league.p3': 'Resets every cycle ({day}). No relegation.',
    'eng.weekday.mon': 'Monday',
    'eng.weekday.tue': 'Tuesday',
    'eng.weekday.wed': 'Wednesday',
    'eng.weekday.thu': 'Thursday',
    'eng.weekday.fri': 'Friday',
    'eng.weekday.sat': 'Saturday',
    'eng.weekday.sun': 'Sunday',
    'eng.league.join': 'Join this week',
    'eng.league.leave': 'Leave the league',
    'eng.league.group': 'Your group this week',
    'eng.league.days': '{n} of {total} days',
    'eng.league.you': 'You',
    'eng.league.metric': 'Days with a Daily Check this week',
    'eng.league.note': 'The whole group celebrates consistency together at the end of the cycle.',

    'eng.recap.title': 'Recap',
    'eng.recap.subtitle': 'Your period at a glance.',
    'eng.recap.entry': 'Monthly and yearly recap',
    'eng.recap.month': 'Month',
    'eng.recap.year': 'Year',
    'eng.recap.avg': 'Average Life Score',
    'eng.recap.streak': 'Longest streak',
    'eng.recap.streakValue': '{n} days',
    'eng.recap.checkDays': 'Daily Check days',
    'eng.recap.habit': 'Most consistent habit',
    'eng.recap.activity': 'Main activity',
    'eng.recap.community': 'In the community',
    'eng.recap.communityValue': '{posts} posts · {challenges} challenges',
    'eng.recap.highlights': 'Highlights',
    'eng.recap.card': 'Your card to share',
    'eng.recap.share': 'Share recap',

    'eng.share.title': 'Share Life Score',
    'eng.share.subtitle': 'You decide whether and where to share.',
    'eng.share.entry': 'Share Life Score',
    'eng.share.today': "Today's Life Score",
    'eng.share.streak': '{n}-day streak',
    'eng.share.includeStreak': 'Include streak',
    'eng.share.includeHabits': 'Include completed habits',
    'eng.share.habits': '{n} of 6 habits',
    'eng.share.privacy': 'Only what you turn on here goes on the card. Body metrics, biomarkers and mood are never included.',
    'eng.share.button': 'Share',
    'eng.share.sheet': 'Share to',
    'eng.share.stories': 'Instagram Stories',
    'eng.share.whatsapp': 'WhatsApp',
    'eng.share.save': 'Save image',
    'eng.share.ready': 'Image ready',
    'eng.share.readyBody': 'In the app, the image is handed to {target} through the system share sheet. Nothing is posted until you confirm there.',
    'eng.share.format': '9:16 format, made for Stories and status.',

    'eng.photos.title': 'Progress photos',
    'eng.photos.subtitle': 'Private by default. You decide when to share.',
    'eng.first.hydrationNoTarget': 'Personalized goal not calculated yet',
    'eng.first.hydrationNoTargetHint':
      'Add your weight whenever you like to calculate your daily hydration goal.',
    'eng.photos.entry': 'Progress photos',
    'eng.photos.private': 'Private',
    'eng.photos.privateNote': "These photos are private by default and don't appear on your profile, in the community or in the Admin.",
    'eng.photos.compare': 'Compare',
    'eng.photos.first': 'Start',
    'eng.photos.latest': 'Latest',
    'eng.photos.timeline': 'Timeline',
    'eng.photos.add': 'Add photo',
    'eng.photos.share': 'Share',
    'eng.photos.shareTitle': 'Share this photo?',
    'eng.photos.shareBody': "It will leave your private space and be visible to whoever you choose in the next step. You can delete it later.",
    'eng.photos.shareConfirm': 'Yes, choose where to share',
    'eng.photos.shareCancel': 'Keep private',
    'eng.photos.alt': 'Progress photo from {date}',
    'eng.photos.addedNote': 'In the app, the camera or photo library opens here. The photo is saved as private.',

    'eng.first.title': 'Your first Daily Check',
    'eng.first.body': "Tap what you've already done today. It takes under a minute.",
    'eng.first.mood': 'How are you feeling now?',
    'eng.first.progress': '{n} Bee today',
    'eng.first.continue': 'Finish and continue',
    'eng.first.skip': 'Do it later',
    'eng.first.hint': 'You can adjust everything later in the Check tab.',

    'eng.auto.title': 'Automatic habit input',
    'eng.auto.body': 'In the future, Apple Health and Google Health Connect may suggest completed habits. Nothing is active now.',
    'eng.auto.confirm': 'When available, you confirm each suggestion before any Bee is counted, and you can turn it off anytime.',
    'eng.auto.from': 'From: {providers}',
  },
}
